// Cvanto Search Phase 4B — Buyer Request detail page (/request/:requestId).
// Two viewers: the requester (can cancel/mark fulfilled) and a matched
// Business (can message/open the requester's profile). Backend
// (server/routes/buyerRequests.js) is the sole source of truth for both
// content and authorization — this page never guesses access from the
// frontend, only renders what GET /:requestId already decided to return.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { ArrowLeft, LoaderCircle } from "lucide-react";
import { getAccessToken } from "../../awsAuth";
import GlassButton from "../UI/GlassButton";
import "./BuyerRequestDetail.scss";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" && window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const STATUS_LABELS = {
  active: "Активний",
  fulfilled: "Виконано",
  cancelled: "Скасовано",
  expired: "Завершився",
};

function statusLabel(effectiveStatus) {
  return STATUS_LABELS[effectiveStatus] || effectiveStatus || "";
}

function statusTone(effectiveStatus) {
  if (effectiveStatus === "active") return "active";
  if (effectiveStatus === "fulfilled") return "success";
  return "muted";
}

function formatBudget(budget) {
  if (!budget) return "";
  const currency = budget.currency === "UAH" ? "грн" : budget.currency || "";
  if (budget.min != null && budget.max != null) return `${budget.min}–${budget.max} ${currency}`.trim();
  if (budget.max != null) return `до ${budget.max} ${currency}`.trim();
  if (budget.min != null) return `від ${budget.min} ${currency}`.trim();
  return "";
}

function formatNeededBy(neededBy) {
  if (!neededBy) return "";
  if (neededBy.date && neededBy.time) return `${neededBy.date}, ${neededBy.time}`;
  if (neededBy.date) return neededBy.date;
  if (neededBy.time) return neededBy.time;
  return "";
}

function formatDate(ms) {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString("uk-UA", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

// In-app confirmation, not window.confirm() — same portal/backdrop/card
// shape as src/components/Profile/BusinessProfile/modules/BeforeAfter.js's
// delete-confirm sheet (this project's established pattern for a terminal,
// hard-to-undo action): disabled-while-running, inline error, closes only
// after the backend actually confirms.
function ConfirmSheet({ title, text, busy, error, onCancel, onConfirm, confirmLabel }) {
  return createPortal(
    <div
      className="brConfirm__backdrop"
      onClick={busy ? undefined : onCancel}
      role="dialog"
      aria-modal="true"
    >
      <div className="brConfirm__card" onClick={(e) => e.stopPropagation()}>
        <div className="brConfirm__title">{title}</div>
        <div className="brConfirm__text">{text}</div>

        {error && <div className="brConfirm__error">{error}</div>}

        <div className="brConfirm__actions">
          <button type="button" className="brConfirm__cancel" onClick={onCancel} disabled={busy}>
            Скасувати
          </button>
          <button type="button" className="brConfirm__confirm" onClick={onConfirm} disabled={busy}>
            {busy ? <LoaderCircle size={16} className="brConfirm__spinner" /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function BuyerRequestDetail() {
  const { requestId } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [pendingAction, setPendingAction] = useState(null); // 'cancelled' | 'fulfilled' | null
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: "" }));

    try {
      const token = await getAccessToken().catch(() => null);
      const res = await fetch(`${API_BASE}/api/buyer-requests/${encodeURIComponent(requestId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const message =
          res.status === 404
            ? "Запит не знайдено"
            : res.status === 403
              ? "Немає доступу до цього запиту"
              : data?.error || "Не вдалося завантажити запит";
        setState({ loading: false, error: message, data: null });
        return;
      }

      setState({ loading: false, error: "", data });
    } catch (e) {
      setState({ loading: false, error: "Не вдалося завантажити запит", data: null });
    }
  }, [requestId]);

  useEffect(() => {
    load();
  }, [load]);

  const runStatusChange = async (nextStatus) => {
    setActionBusy(true);
    setActionError("");

    try {
      const token = await getAccessToken().catch(() => null);
      const res = await fetch(
        `${API_BASE}/api/buyer-requests/${encodeURIComponent(requestId)}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status: nextStatus }),
        }
      );

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || "Не вдалося оновити статус запиту");
      }

      // Update UI only from the successful backend response — no
      // optimistic change before the request actually succeeds.
      setState((prev) => ({ ...prev, data: { ...prev.data, ...data } }));
      setPendingAction(null);
    } catch (e) {
      setActionError(e?.message || "Не вдалося оновити статус запиту");
    } finally {
      setActionBusy(false);
    }
  };

  if (state.loading) {
    return (
      <main className="brDetail brDetail--state">
        <div className="brDetail__spinner">
          <LoaderCircle size={28} className="brConfirm__spinner" />
        </div>
      </main>
    );
  }

  if (state.error || !state.data) {
    return (
      <main className="brDetail brDetail--state">
        <button
          type="button"
          className="brDetail__back"
          onClick={() => navigate(-1)}
          aria-label="Назад"
        >
          <ArrowLeft size={22} strokeWidth={2.5} />
        </button>
        <div className="brDetail__errorText">{state.error || "Запит не знайдено"}</div>
      </main>
    );
  }

  const { data } = state;
  const isRequester = data.viewerRole === "requester";
  const isMatchedBusiness = data.viewerRole === "matched_business";
  const isActive = data.effectiveStatus === "active";

  return (
    <main className="brDetail">
      <header className="brDetail__hero">
        <button
          type="button"
          className="brDetail__back"
          onClick={() => navigate(-1)}
          aria-label="Назад"
        >
          <ArrowLeft size={22} strokeWidth={2.5} />
        </button>
        <h1>Запит</h1>
        <span className={`brDetail__badge is-${statusTone(data.effectiveStatus)}`}>
          {statusLabel(data.effectiveStatus)}
        </span>
      </header>

      <section className="brDetail__requester">
        {data.requester?.avatar ? (
          <img className="brDetail__avatar" src={data.requester.avatar} alt="" />
        ) : (
          <div className="brDetail__avatar brDetail__avatar--placeholder" />
        )}
        <div className="brDetail__requesterName">
          {data.requester?.name || data.requester?.username || "Користувач"}
        </div>
      </section>

      <section className="brDetail__card">
        <p className="brDetail__query">{data.query}</p>

        <div className="brDetail__meta">
          {data.city && <span className="brDetail__chip">{data.city}</span>}
          {formatBudget(data.budget) && (
            <span className="brDetail__chip">{formatBudget(data.budget)}</span>
          )}
          {formatNeededBy(data.neededBy) && (
            <span className="brDetail__chip">{formatNeededBy(data.neededBy)}</span>
          )}
        </div>

        <div className="brDetail__created">Створено: {formatDate(data.createdAt)}</div>
      </section>

      {isMatchedBusiness && (
        <div className="brDetail__actions">
          <GlassButton
            as="link"
            to={`/messages?to=${encodeURIComponent(data.requester?.id || "")}`}
            size="md"
          >
            Написати
          </GlassButton>
          <GlassButton
            as="link"
            to={`/profile/${encodeURIComponent(data.requester?.id || "")}`}
            size="md"
            variant="neutral"
          >
            Перейти до профілю
          </GlassButton>
        </div>
      )}

      {isRequester && isActive && (
        <div className="brDetail__actions">
          <GlassButton onClick={() => setPendingAction("fulfilled")} size="md">
            Позначити виконаним
          </GlassButton>
          <GlassButton
            onClick={() => setPendingAction("cancelled")}
            size="md"
            variant="neutral"
          >
            Скасувати запит
          </GlassButton>
        </div>
      )}

      {pendingAction && (
        <ConfirmSheet
          title={pendingAction === "fulfilled" ? "Позначити запит виконаним?" : "Скасувати запит?"}
          text={
            pendingAction === "fulfilled"
              ? "Бізнеси, які вже отримали цей запит, побачать, що його виконано."
              : "Запит стане неактивним. Цю дію не можна скасувати."
          }
          busy={actionBusy}
          error={actionError}
          confirmLabel={pendingAction === "fulfilled" ? "Виконано" : "Скасувати"}
          onCancel={() => {
            if (actionBusy) return;
            setPendingAction(null);
            setActionError("");
          }}
          onConfirm={() => runStatusChange(pendingAction)}
        />
      )}
    </main>
  );
}
